import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, apiFetch } from './client'

export type User = {
  id: number
  email: string
  is_admin: boolean
  created_at: string
  totp_enabled: boolean
  //: Sólo true en producción — ver Settings.webauthn_rp_id en el backend.
  //: No es del usuario, es si la feature existe en este entorno.
  webauthn_available: boolean
  //: Riesgo de CARTERA — agregado entre TODOS los bots del usuario, a
  //: diferencia de los límites de un RiskProfile, que son por bot. `null` =
  //: sin límite. Ver app/live/portfolio_risk.py.
  max_symbol_notional: string | null
  max_gross_notional: string | null
}

/** Resultado del primer factor (docs/plan_bot_trading.md §3.4.1). Con
 * `totp_required`, todavía no hay sesión ni cookie — `pending_token` es lo
 * único que hace falta para el segundo paso, en /auth/totp/verify. */
export type LoginResult =
  | { status: 'ok'; user: User }
  | { status: 'totp_required'; pending_token: string }

export type HealthStatus = {
  status: 'ok' | 'degraded'
  environment: string
  database: { status: 'ok' | 'error'; detail: string | null }
  redis: { status: 'ok' | 'error'; detail: string | null }
  //: Mismo campo que User.webauthn_available — este existe para la pantalla
  //: de login, antes de cualquier sesión.
  webauthn_available: boolean
}

export type WebauthnCredential = {
  id: number
  nickname: string
  device_type: string
  created_at: string
  last_used_at: string | null
}

export const currentUserKey = ['auth', 'me'] as const

/** Usuario actual. `null` cuando no hay sesión — un 401 acá es respuesta válida, no error. */
export function useCurrentUser() {
  return useQuery({
    queryKey: currentUserKey,
    queryFn: async () => {
      try {
        return await apiFetch<User>('/auth/me')
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null
        throw error
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
    // `/auth/me` es lo único que desliza la cookie CSRF (ver el comentario
    // en el handler) — sin poll periódico, una pestaña que queda abierta
    // más que `session_ttl_seconds` sin recargar nunca vuelve a pedirla, la
    // cookie vence en silencio, y la sesión sigue viéndose "activa" hasta
    // que la primera mutación (2FA, cambiar contraseña, etc.) rebota con
    // 403 CSRF. `refetchOnWindowFocus` está apagado a nivel global, así que
    // esto no puede depender de foco/blur para disparar — y sigue corriendo
    // en segundo plano por la misma razón: una pestaña minimizada es
    // exactamente el caso que se está cubriendo.
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: true,
  })
}

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (credentials: { email: string; password: string; turnstile_token: string }) =>
      apiFetch<LoginResult>('/auth/login', { method: 'POST', body: credentials }),
    onSuccess: (result) => {
      if (result.status === 'ok') queryClient.setQueryData(currentUserKey, result.user)
    },
  })
}

/** Siempre "éxito" con el mismo mensaje, exista o no la cuenta — el backend
 *  mitiga enumeración de usuarios respondiendo igual en los dos casos. */
export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) =>
      apiFetch<{ message: string }>('/auth/forgot-password', { method: 'POST', body: { email } }),
  })
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (payload: { token: string; new_password: string }) =>
      apiFetch<{ message: string }>('/auth/reset-password', { method: 'POST', body: payload }),
  })
}

/** Segundo factor: TOTP o, si no queda otra, un código de recuperación —
 * el backend prueba las dos cosas contra el mismo campo `code`. */
export function useTotpVerify() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { pending_token: string; code: string }) =>
      apiFetch<LoginResult>('/auth/totp/verify', { method: 'POST', body: payload }),
    onSuccess: (result) => {
      if (result.status === 'ok') queryClient.setQueryData(currentUserKey, result.user)
    },
  })
}

/** Re-autenticación (docs/plan_bot_trading.md §3.4.1): alta de credencial de
 * exchange, borrarla, pasar un bot a `live`, etc. Vale por
 * `step_up_ttl_seconds` (5 minutos) — pasado eso, la próxima acción sensible
 * la vuelve a pedir. */
export function useStepUp() {
  return useMutation({
    mutationFn: (password: string) =>
      apiFetch<{ message: string }>('/auth/step-up', {
        method: 'POST',
        body: { password },
      }),
  })
}

/** El backend exige step-up (`RequireStepUp`) además de la contraseña
 *  actual en el body — dos verificaciones de password distintas, ver
 *  ChangePasswordDialog.tsx para cómo se maneja esa redundancia en la UX. */
export function useChangePassword() {
  return useMutation({
    mutationFn: (payload: { current_password: string; new_password: string }) =>
      apiFetch<{ message: string }>('/security/change-password', { method: 'POST', body: payload }),
  })
}

/** Genera un secreto nuevo (reemplaza cualquiera pendiente sin confirmar —
 *  cubre el reintento de "until escaneé mal el QR") y no queda activo hasta
 *  `useTotpConfirm`. */
export function useTotpSetup() {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ secret: string; provisioning_uri: string }>('/security/totp/setup', {
        method: 'POST',
      }),
  })
}

/** Activa el 2FA y devuelve los códigos de recuperación — en texto plano,
 *  una sola vez; después sólo quedan hasheados. */
export function useTotpConfirm() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (code: string) =>
      apiFetch<{ codes: string[] }>('/security/totp/confirm', { method: 'POST', body: { code } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: currentUserKey })
    },
  })
}

export function useTotpDisable() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (password: string) =>
      apiFetch<{ message: string }>('/security/totp/disable', {
        method: 'POST',
        body: { password },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: currentUserKey })
    },
  })
}

/** Sube el límite de exposición de cartera — por eso pide step-up, mismo
 *  criterio que crear un RiskProfile ("sube un límite de exposición"). Los
 *  dos campos van siempre juntos: `null` borra el límite, no "no tocarlo". */
export function useUpdateRiskLimits() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { max_symbol_notional: string | null; max_gross_notional: string | null }) =>
      apiFetch<User>('/auth/me/risk-limits', { method: 'PATCH', body: payload }),
    onSuccess: (user) => {
      queryClient.setQueryData(currentUserKey, user)
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch<{ message: string }>('/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      queryClient.setQueryData(currentUserKey, null)
      void queryClient.invalidateQueries()
    },
  })
}

export function useHealth() {
  return useQuery({
    queryKey: ['health'],
    queryFn: () => apiFetch<HealthStatus>('/health'),
    refetchInterval: 30_000,
  })
}

// ── WebAuthn / llaves de acceso — sólo en producción, ver User/HealthStatus
// .webauthn_available. Fuera de producción el backend devuelve 404 en los
// seis endpoints: los componentes que llaman a estos hooks tienen que
// chequear ese caso ellos mismos (no hay nada especial acá que lo esconda).

export function useWebauthnRegisterOptions() {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ options: Record<string, unknown> }>('/security/webauthn/register/options', {
        method: 'POST',
      }),
  })
}

export function useWebauthnRegisterVerify() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { credential: Record<string, unknown>; nickname: string }) =>
      apiFetch<WebauthnCredential>('/security/webauthn/register/verify', {
        method: 'POST',
        body: payload,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['webauthn', 'credentials'] })
    },
  })
}

export function useWebauthnCredentials() {
  return useQuery({
    queryKey: ['webauthn', 'credentials'],
    queryFn: () => apiFetch<WebauthnCredential[]>('/security/webauthn/credentials'),
    retry: false,
  })
}

export function useWebauthnDeleteCredential() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ message: string }>(`/security/webauthn/credentials/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['webauthn', 'credentials'] })
    },
  })
}

/** Paso de login, sin sesión — mismo `pending_token` que ya arma /auth/login
 *  para el paso de TOTP. */
export function useWebauthnAuthenticateOptions() {
  return useMutation({
    mutationFn: (pendingToken: string) =>
      apiFetch<{ options: Record<string, unknown> }>('/auth/webauthn/authenticate/options', {
        method: 'POST',
        body: { pending_token: pendingToken },
      }),
  })
}

export function useWebauthnAuthenticateVerify() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { pending_token: string; credential: Record<string, unknown> }) =>
      apiFetch<LoginResult>('/auth/webauthn/authenticate/verify', {
        method: 'POST',
        body: payload,
      }),
    onSuccess: (result) => {
      if (result.status === 'ok') queryClient.setQueryData(currentUserKey, result.user)
    },
  })
}
